/**
 * 权限管理 API
 */
import { request } from './api';

// --- 类型定义 ---

export interface TenantSettings {
  id: string;
  tenant_name: string;
  tenant_status: string;
  plan_type: string;
  total_seats: number;
  used_seats: number;
  enabled_modules: string[];
  default_modules_new_user: string[];
  feishu_app_id?: string;
  feishu_sync_enabled: boolean;
  last_feishu_sync_at?: string;
}

export interface DepartmentPermission {
  id: string;
  permission_id?: string;
  open_department_id: string;
  name: string;
  member_count: number;
  modules: string[];
  connector_subs: string[];
  color: string;
}

export interface TenantMember {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar_url: string;
  avatar_color: string;
  avatar_text: string;
  role: 'admin' | 'member';
  department_id: string;
  department_name: string;
  job_title: string;
  status: 'active' | 'inactive' | 'pending';
  modules: string[];
  connector_subs: string[];
  last_active_at: string;
  created_at: string;
}

export interface MemberPermissions {
  member_id: string;
  role: string;
  is_admin: boolean;
  is_super_admin: boolean;
  tenant_name: string;
  tenant_modules: string[];
  member_modules: string[];
  connector_subs: string[];
  effective_modules: string[];
  effective_connector_subs: string[];
}

export interface ModuleInfo {
  id: string;
  name: string;
  icon: string;
  admin_only: boolean;
}

export interface ConnectorSubInfo {
  id: string;
  name: string;
  icon: string;
}

// --- 企业设置 ---

export async function getTenantSettings(): Promise<TenantSettings> {
  return request<TenantSettings>('/permissions/tenant');
}

export async function updateTenantSettings(updates: Partial<TenantSettings>): Promise<TenantSettings> {
  return request<TenantSettings>('/permissions/tenant', {
    method: 'PUT',
    body: updates,
  });
}

// --- 部门权限 ---

export async function listDepartmentPermissions(): Promise<DepartmentPermission[]> {
  return request<DepartmentPermission[]>('/permissions/departments');
}

export async function setDepartmentPermission(
  deptId: string,
  modules: string[],
  connectorSubs: string[]
): Promise<void> {
  await request(`/permissions/departments/${deptId}`, {
    method: 'PUT',
    body: { modules, connector_subs: connectorSubs },
  });
}

// --- 成员管理 ---

export async function listMembers(params?: {
  dept_id?: string;
  role?: string;
  status?: string;
  q?: string;
  page?: number;
  page_size?: number;
}): Promise<TenantMember[]> {
  const query = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) query.set(key, String(value));
    });
  }
  const result = await request<{ items: TenantMember[]; total: number }>(`/permissions/members?${query}`);
  return result?.items || [];
}

export async function getMember(memberId: string): Promise<TenantMember> {
  return request<TenantMember>(`/permissions/members/${memberId}`);
}

export async function setMemberModules(
  memberId: string,
  modules: string[],
  connectorSubs: string[]
): Promise<void> {
  await request(`/permissions/members/${memberId}/modules`, {
    method: 'PUT',
    body: { modules, connector_subs: connectorSubs },
  });
}

export async function setMemberRole(memberId: string, role: 'admin' | 'member'): Promise<void> {
  await request(`/permissions/members/${memberId}/role`, {
    method: 'PUT',
    body: { role },
  });
}

export async function getMemberPermissions(memberId: string): Promise<MemberPermissions> {
  return request<MemberPermissions>(`/permissions/members/${memberId}/permissions`);
}

export async function getMyPermissions(): Promise<MemberPermissions> {
  return request<MemberPermissions>('/permissions/me');
}

// --- 权限检查 ---

export async function checkModuleAccess(memberId: string, moduleId: string): Promise<boolean> {
  const res = await request<{ allowed: boolean }>(
    `/permissions/check?member_id=${memberId}&module_id=${moduleId}`
  );
  return res.allowed;
}

// --- 模块列表 ---

export async function getModuleList(): Promise<{
  modules: ModuleInfo[];
  connector_subs: ConnectorSubInfo[];
}> {
  return request('/permissions/modules');
}

// --- 同步 ---

export async function syncMembersFromFeishu(): Promise<{ synced: number; message: string }> {
  const res = await request<{ synced: number; message: string }>('/permissions/sync-from-feishu', {
    method: 'POST',
  });
  return res;
}

// --- 辅助函数 ---

export const MODULE_LABELS: Record<string, string> = {
  tools: '超级工具',
  wins: '战果发布',
  knowledge: '企业知识库',
  apps: '企业应用',
  connectors: '连接器',
  org: '部门人员与权限',
};

export const CONNECTOR_SUB_LABELS: Record<string, string> = {
  files: '企业文件',
  disk: '员工磁盘',
  feishu: '飞书',
  saas: 'SaaS',
  email: '邮箱',
};

export function getModuleLabel(moduleId: string): string {
  return MODULE_LABELS[moduleId] || moduleId;
}

export function getConnectorSubLabel(subId: string): string {
  return CONNECTOR_SUB_LABELS[subId] || subId;
}
